import {
    type BaseEvent,
    type Checklist,
    type ChecklistDirection,
    type ChecklistKey,
    type ChildEvent,
    type Confirmation,
    type DbBaseEvent,
    type DbChildEvent,
    type EventFlag,
    type EventFlags,
    type EventResource,
    type FlightData,
    type GenericFile,
    type LegChecklist,
    type LegOpsNote,
    type Resource,
    type TravelData,
} from '@eflight/shared';
import { EventType, isNone, isSome, objectKeys, ResourceRole, ResourceType } from '@eflight/shared';
import { produce } from 'immer';
import * as uuid from 'uuid';

import {
    type CancelledLegs,
    type CreateCancelledLegsInput,
    type CreateQuotationInput,
    type CreateQuotationResourceInput,
    type CreateReservationInput,
    type CreateReservationResourceInput,
} from '../../../API';
import { type DbCreated, type WithId } from '../../../Datasource/LocalDB/types';
import { type RfqState } from '../../Avinode/model/RfqType';
import { type Fleet2AircraftItem } from '../../screens/Fleet2/Fleet2Datasource';
// import { isNone, isSome, Maybe, objectKeys } from 'utils';
import { type CreateChildEvent, type CreateReservation } from '../Datasources';
import { type QuoteCalcData } from '../screen/Calendar/ReservationForm/QuotationCalculator';
import { type ExtraLinesData_depracated } from '../screen/Calendar/ReservationForm/QuotationCalculator/_depracated/extraLines';

// TODO Hotifix: If isNotDeleted is imported from ../Datasources/calendar, app crashes
// import { isNotDeleted } from '../Datasources/calendar';
function isNotDeleted(item: { deleted?: boolean | null }): boolean {
    const { deleted } = item;

    return !isSome(deleted) || !deleted;
}

export type PaxData = {
    paxDetails: PaxDetail[];
};

export type DateRange = { start: Date; end: Date };

export type PaxDetail = {
    id: string;
    surName: string | undefined;
    firstName: string | undefined;
    dateOfBirth: string | undefined;
    placeOfBirth: string | undefined;
    nationality: string | undefined;
    documentNumber: string | undefined;
    documentExpiry: string | undefined;
    documentType: DocumentType | undefined;
    sex: SexType;
    contactId?: string | undefined;

    excludedLegs: PaxLeg[];
};

export type PaxLeg = {
    id: string;
};

export enum SexType {
    female = 'female',
    male = 'male',
}

export enum DocumentType {
    id = 'id',
    pax = 'pax',
}

export type CalendarLine = {
    reservations: Reservation[];
    resource: Resource;
    maintenance?: MaintenanceModel;
    limits?: Fleet2AircraftItem[] | null;
    readonly: boolean;
};

export enum FlightType {
    'multileg' = 'multileg',
    'simple' = 'simple',
}

export type { Resource } from '@eflight/shared';
export { ResourceType } from '@eflight/shared';
export { ResourceRole } from '@eflight/shared';

export const pilotResourceRoles = [ResourceRole.pic, ResourceRole.fo];

export function resourceIsPilot(resource: EventResource) {
    return pilotResourceRoles.includes(resource.role);
}

export function resourceIsCrew(resource: EventResource) {
    return resourceIsPilot(resource) || resource.role === ResourceRole.person;
}

export function resourceIsAircraft(resource: EventResource) {
    return resource.role === ResourceRole.aircraft;
}

export function resourceIsPic(resource: EventResource) {
    return resource.role === ResourceRole.pic;
}

export function resourceIsFo(resource: EventResource) {
    return resource.role === ResourceRole.fo;
}

export function findAircraftResource(resources: EventResource[] | undefined) {
    return resources?.find((res) => resourceIsAircraft(res) && !res.deleted)?.resource;
}

export function findPicResource(resources: EventResource[] | undefined) {
    return resources?.find((res) => resourceIsPic(res) && !res.deleted)?.resource;
}

export function findFoResource(resources: EventResource[] | undefined) {
    return resources?.find((res) => resourceIsFo(res) && !res.deleted)?.resource;
}

export type { SchedulingCustomer } from '@eflight/shared';

export type { SchedulingExternalAircraft } from '@eflight/shared';

export { EventType } from '@eflight/shared';

export { Permission } from '@eflight/shared';

export { TravelMethod } from '@eflight/shared';

export type { TravelData } from '@eflight/shared';

export enum Action {
    create = 'C',
    delete = 'D',
    update = 'U',
}

export type CreateBaseEvent = Omit<BaseEvent, 'createdAt' | 'id'>;

export type { BaseEvent } from '@eflight/shared';

export type { Confirmation } from '@eflight/shared';

const confirmationFlightPlanKeys: Record<keyof NonNullable<Confirmation['flightPlan']>, null> = {
    vfr: null,
    ifr: null,
    vfrIfr: null,
    ifrVfr: null,
};
export const CONFIRMATION_FLIGHT_PLAN_KEYS = objectKeys(confirmationFlightPlanKeys);

function checklistBackwardCompatibility(legChecklist: LegChecklist | undefined): LegChecklist | undefined {
    if (isNone(legChecklist)) {
        return undefined;
    }

    return produce(legChecklist, (draft) => {
        objectKeys(draft).forEach((direction) => {
            const checklist = draft[direction];
            if (checklist) {
                objectKeys(checklist).forEach((checklistKey) => {
                    checklist[checklistKey]?.forEach((confirmation: Confirmation & { time?: Date }) => {
                        if (isNone(confirmation.serviceTime) && isSome(confirmation.time)) {
                            confirmation.serviceTime = confirmation.time;
                            delete confirmation.time;
                        }
                    });
                });
            }
        });
    });
}

export type ChecklistConfirmationStatus = Confirmation['status'];

export type { Checklist } from '@eflight/shared';

export type QuotationConfirmation<S> = {
    confirmTime: Date;
    status?: S;
    userId?: string;
    note?: string;
};

export const SALES_STATUS = [
    'new_request',
    'quote_sent',
    'pending_confirmation',
    'confirmed_by_customer',
    'approved_by_sales',
    'cancelled',
] as const;
export const CONTRACT_STATUS = ['not_sent', 'sent', 'signed'] as const;
export const PAYMENT_STATUS = ['not_paid', 'pop_block', 'paid', 'prepayment_credit', 'underpaid', 'overpaid'] as const;
export const OPS_STATUS = ['sent_to_ops', 'ack_by_ops', 'confirmed_ok', 'released_by_ops', 'cancelled'] as const;
type SalesStatus = (typeof SALES_STATUS)[number];
type ContractStatus = (typeof CONTRACT_STATUS)[number];
type PaymentStatus = (typeof PAYMENT_STATUS)[number];
type OpsStatus = (typeof OPS_STATUS)[number];

export type QuotationChecklist = {
    salesStatus?: Array<QuotationConfirmation<SalesStatus>>;
    contract?: Array<QuotationConfirmation<ContractStatus>>;
    payment?: Array<QuotationConfirmation<PaymentStatus>>;
    opsStatus?: Array<QuotationConfirmation<OpsStatus>>;
};

export type QuotationChecklistKey = NonNullable<keyof QuotationChecklist>;

export type { ChecklistKey } from '@eflight/shared';
export type ChecklistPermanentKey = keyof Pick<Checklist, 'adCategorization' | 'generalDispatchNotes' | 'notam' | 'wx'>;
export type ChecklistConfigurableKey = Exclude<ChecklistKey, ChecklistExtraKey | ChecklistPermanentKey>;
export type ChecklistExtraKey = keyof Pick<
    Checklist,
    'airportService' | 'avinode' | 'catering' | 'countryPermit' | 'flightPlan' | 'taxi'
>;

const permanentKeys: Record<ChecklistPermanentKey, null> = {
    generalDispatchNotes: null,
    adCategorization: null,
    wx: null,
    notam: null,
};
const configurableKeys: Record<ChecklistConfigurableKey, null> = {
    slotPprRequired: null,
    handling: null,
    immigrationCustomsAvailable: null,
    fuelAvgasAvailable: null,
    fuelJetAvailable: null,
    gendecRequired: null,
    petsPermitRequired: null,
    boardToBoardService: null,
    vipTerminal: null,
};
const extraKeys: Record<ChecklistExtraKey, null> = {
    taxi: null,
    catering: null,
    flightPlan: null,
    airportService: null,
    avinode: null,
    countryPermit: null,
};

export const checklistKeyOrdering: ChecklistKey[] = [
    'generalDispatchNotes',
    'adCategorization',
    'wx',
    'notam',
    'flightPlan',
    'countryPermit',
    'slotPprRequired',
    'handling',
    'airportService',
    'immigrationCustomsAvailable',
    'fuelAvgasAvailable',
    'fuelJetAvailable',
    'gendecRequired',
    'taxi',
    'catering',
    'petsPermitRequired',
    'boardToBoardService',
    'vipTerminal',
];

export const CHECKLIST_PERMANENT_KEYS = objectKeys(permanentKeys);
export const CHECKLIST_CONFIGURABLE_KEYS = objectKeys(configurableKeys);
export const CHECKLIST_EXTRA_KEYS = objectKeys(extraKeys);

export type { LegChecklist } from '@eflight/shared';

export type { LegOpsNote } from '@eflight/shared';

export type { ChecklistDirection } from '@eflight/shared';

export type LegOpsDirection = NonNullable<keyof LegOpsNote>;

export type ChecklistConfirmationData = Pick<
    Confirmation,
    | 'aircraftResourceId'
    | 'confirmMethod'
    | 'foResourceId'
    | 'icaoFrom'
    | 'icaoTo'
    | 'notamsFetchedAt'
    | 'note'
    | 'number'
    | 'picResourceId'
    | 'providerId'
    | 'serviceTime'
>;

export function pickChecklistConfirmationData(confirmation: Confirmation): ChecklistConfirmationData {
    return {
        note: confirmation.note,
        number: confirmation.number,
        serviceTime: confirmation.serviceTime,
        providerId: confirmation.providerId,
        confirmMethod: confirmation.confirmMethod,
        icaoFrom: confirmation.icaoFrom,
        icaoTo: confirmation.icaoTo,
        picResourceId: confirmation.picResourceId,
        foResourceId: confirmation.foResourceId,
        aircraftResourceId: confirmation.aircraftResourceId,
    };
}

export const CHECKLIST_DIRECTIONS: ChecklistDirection[] = [
    'departure',
    'arrival',
    'alternate1',
    'alternate2',
    'alternate3',
];

export function directionIsAllowableChecklistDirection(direction: string): direction is ChecklistDirection {
    return CHECKLIST_DIRECTIONS.includes(direction as ChecklistDirection);
}

export function directionIsAllowableOpsNotesDirection(direction: string): direction is LegOpsDirection {
    return ['arrival', 'departure'].includes(direction);
}

export type { FlightPlanData } from '@eflight/shared';

export type { FlightLevelPrices } from '@eflight/shared';

export type { ChildEvent } from '@eflight/shared';

export type SendRecord = {
    readonly userId: string;
    readonly date: Date;
};

type Price<Source extends 'avinode' | 'eflight'> = {
    value?: number;
    currency?: string;
    source: Source;
};

export interface Reservation extends BaseEvent {
    events?: ChildEvent[];
    readonly isExternal?: boolean | null;
    readonly avinode?: {
        rfqExternalId?: string;
        availability?: boolean;
        tripId?: string;
        state?: RfqState;
        createdAt?: Date;
        avinodeLink?: string;
        customerId?: string;
        customerName?: string;
        requestedPrice?: Price<'avinode'>; // requested cena může být pouze z avinode
        confirmedPrice?: Price<'avinode' | 'eflight'>;
    };
    readonly contactId?: string;
    readonly contactName?: string;
    readonly flightType?: FlightType;
    readonly reservationCode?: string | null;
    readonly fuelStart?: number;
    readonly eventsSimple?: Array<{ location: string }>;
    readonly requestedFuel?: number;
    readonly esimatedHours?: number;
    readonly ownerApproval?: boolean;
    readonly customerBriefingSent?: SendRecord;
    readonly flightOrderSent?: SendRecord;
    readonly invoiceSent?: SendRecord;
    readonly contractSent?: SendRecord;
    readonly quotationCalculation?: QuoteCalcData;
    readonly paxData?: PaxData;
    readonly quotationChecklist?: QuotationChecklist;
    readonly files?: GenericFile[];
    readonly diffCrewForEachEvent?: DbReservation['diffCrewForEachEvent'];
    readonly state?: string | null;
    readonly avinodeRfqId?: string | null;
    readonly quotationId?: string | null;
    readonly isDutyPreview?: string | null;
    readonly cancelledLegs: CancelledLegs[] | undefined;
    readonly slsCancelled?: boolean | null;
    readonly opsCancelled?: boolean | null;
    readonly flightData?: FlightData;
    // Set on maintenance reservations that are created from / linked to a maintenance work order.
    readonly workOrderId?: string | null;
    // Set on travel reservations: how the travellers get there. The documents themselves live in
    // the ReservationDocument table, not here.
    readonly travel?: TravelData;
}

export type { QuoteCalcData };

export function isCreateChildEvent(event: CreateChildEvent | CreateReservation): event is CreateChildEvent {
    return event.hasOwnProperty('reservationId');
}

export function isChildEvent(event: ChildEvent | Reservation): event is ChildEvent {
    return event.hasOwnProperty('reservationId');
}

export function isReservation(event: ChildEvent | Reservation): event is Reservation {
    return !isChildEvent(event);
}

export type ReservationData = Omit<CreateReservationInput & WithId, '_lastChangedAt' | 'createdAt'> & {
    readonly createdAt: Date;
    readonly reservation: Reservation;
};

export type CreatedCancelledLeg = CreateCancelledLegsInput & DbCreated;
export type CreatedQuotation = CreateQuotationInput & DbCreated;
export type CreatedReservation = CreateReservationInput & DbCreated;

export function reservationDataFromDb(
    params:
        | {
              isQuotation: false;
              input: CreatedReservation;
              cancelledLegs?: CancelledLegs[] | null | undefined;
          }
        | {
              isQuotation: true;
              input: CreatedQuotation;
          }
): ReservationData | undefined {
    if (params.isQuotation ? isNone(params.input.quotationData) : isNone(params.input.reservationData)) {
        // TODO: Je správné v tom případě vracet undefined, nebo se má reagovat jinak?
        return undefined;
    }

    try {
        const reservationId = params.input.id;
        const result: ReservationData = {
            ...params.input,
            createdAt: new Date(params.input.createdAt!),
            deleted: params.input.deleted,
            reservation: {
                ...JSON.parse(
                    (params.isQuotation ? params.input.quotationData : params.input.reservationData) ?? '',
                    dateTimeReviver as (key: string, value: any) => any
                ),
                id: reservationId,
                isExternal: params.input.isExternal,
                state: params.input.state,
                avinodeRfqId: params.input.avinodeRfqId,
                dateFrom: new Date(params.input.dateFrom),
                dateTo: new Date(params.input.dateTo),
                reservationCode: params.isQuotation
                    ? params.input.quotationCode?.toUpperCase()
                    : params.input.reservationCode?.toUpperCase(),
                deleted: params.input.deleted,
                quotationCalculation: params.isQuotation
                    ? parseQuoteCalcData(params.input.quotationCalculation)
                    : undefined,
                paxData: safeJsonParse(params.input.paxData ?? '{}', undefined),
                isDutyPreview: params.isQuotation ? params.input.isDutyPreview : undefined,
                cancelledLegs: params.isQuotation ? [] : (params.cancelledLegs?.filter(isSome) ?? []),
                slsCancelled: params.isQuotation ? undefined : params.input.slsCancelled,
                opsCancelled: params.isQuotation ? undefined : params.input.opsCancelled,
                flightData: params.isQuotation
                    ? undefined
                    : isSome(params.input.flightData)
                      ? safeJsonParse<FlightData>(params.input.flightData, { flights: [] })
                      : undefined,
            },
        };

        // [TODO Lukas]: Some child events are missing reservation ID from some reason, I add it manually here
        return {
            ...result,
            reservation: {
                ...result.reservation,
                events: result.reservation.events
                    ? deduplicateReservationEvents(result.reservation.events).map((childEvent) => ({
                          ...childEvent,
                          reservationId,
                          checklist: checklistBackwardCompatibility(childEvent.checklist),
                      }))
                    : undefined,
            },
        };
    } catch {
        return undefined;
    }
}

function parseQuoteCalcData(quoteCalcRawString: string | null | undefined): QuoteCalcData | undefined {
    try {
        if (quoteCalcRawString) {
            const result = JSON.parse(
                quoteCalcRawString,
                dateTimeReviver as (key: string, value: any) => any
            ) as QuoteCalcData & { extraLines?: ExtraLinesData_depracated };

            // Backward compatibility for old data format (extraLines is depracated, only legLines should be used)
            if (isSome(result.extraLines) && isNone(result.legLines)) {
                result.legLines = result.extraLines.map((extraLine) => ({
                    linesData: extraLine,
                    isRemoved: undefined,
                }));
            }

            return result;
        }

        return undefined;
    } catch {
        return undefined;
    }
}

function safeJsonParse<T>(jsonString: string, fallback: T): T {
    try {
        return JSON.parse(jsonString) as T;
    } catch {
        return fallback;
    }
}

function deduplicateReservationEvents(events: ChildEvent[]): ChildEvent[] {
    // when double-save is sent to DB (without updating the _version), all array items are doubled. This deduplicates them
    const existingIdsToIndexes = new Map<string, number>();
    const out = [];
    for (const event of events) {
        if (event.id && existingIdsToIndexes.has(event.id)) {
            const index = existingIdsToIndexes.get(event.id)!;
            out[index] = event;
        } else {
            existingIdsToIndexes.set(event.id, out.length);
            out.push(event);
        }
    }

    return out;
}

export function reservationDataToDb(record: ReservationData, quotationId?: string): CreateReservationInput {
    const { paxData, cancelledLegs, ...withoutQuotationCalc } = record.reservation;
    const dbReservationData = withoutQuotationCalc as DbReservation;

    const statuses = record.reservation.quotationChecklist?.opsStatus;
    const actualOpsStatus = statuses && statuses.length > 0 ? statuses.at(-1) : undefined;

    return {
        id: record.id,
        createdAt: record.createdAt.toISOString(),
        deleted: record.deleted,
        isActive: record.deleted // for indexing purposes only, so it's not used in app model
            ? record._version
                ? null
                : undefined
            : 'x',
        reservationData: JSON.stringify(dbReservationData),
        reservationType: dbReservationData.type, // denormalized from reservationData.type for indexing/filtering without JSON parsing
        _version: record._version,
        dateFrom: record.dateFrom,
        dateTo: record.dateTo,
        isExternal: record.isExternal,
        state: record.state,
        paxData: JSON.stringify(paxData),
        avinodeRfqId: record.avinodeRfqId,
        contactId: record.contactId,
        reservationCode: record.reservationCode,
        quotationId,
        aircraftId: dbReservationData.resources
            .filter((res) => isNotDeleted(res))
            .find((x) => x.role === ResourceRole.aircraft)?.resource.resourceId,
        opsCancelled: actualOpsStatus?.status === 'cancelled',
    };
}

export function quotationDataToDb(record: ReservationData): CreateQuotationInput {
    const { quotationCalculation, paxData, isDutyPreview, cancelledLegs, ...withoutQuotationCalc } = record.reservation;

    const dbReservationData = withoutQuotationCalc as DbReservation;

    const contractStatus = record.reservation?.quotationChecklist?.contract?.slice(-1)[0]?.status;

    return {
        id: record.id,
        createdAt: record.createdAt.toISOString(),
        deleted: record.deleted,
        isActive: record.deleted // for indexing purposes only, so it's not used in app model
            ? record._version
                ? null
                : undefined
            : 'x',
        quotationData: JSON.stringify(dbReservationData),
        quotationCalculation: JSON.stringify(quotationCalculation),
        paxData: JSON.stringify(paxData),
        _version: record._version,
        dateFrom: record.dateFrom,
        dateTo: record.dateTo,
        state: record.state,
        avinodeRfqId: record.avinodeRfqId,
        isExternal: record.isExternal,
        contactId: record.contactId,
        quotationCode: record.reservationCode,
        isDutyPreview: record.reservation.isDutyPreview,
        contractStatus,
    };
}

export function quotationToReservation(
    record: ReservationData,
    params: { userId: string | undefined }
): CreateReservation {
    const { quotationCalculation, paxData, ...withoutQuotationCalc } = record.reservation;

    // Copy fuel uplift prices rates from quotation to reservation
    const reservationWithousQuotation = {
        ...withoutQuotationCalc,
        paxData,
        events: withoutQuotationCalc.events?.map((event, index) => {
            const fuelUpliftPrice = quotationCalculation?.legLines?.[index]?.linesData?.fuelUplift?.rate;
            if (isSome(fuelUpliftPrice)) {
                return {
                    ...event,
                    fuelUpliftPrice,
                };
            }

            return event;
        }),
    };

    return {
        ...reservationWithousQuotation,
        quotationChecklist: {
            opsStatus: [
                {
                    confirmTime: new Date(),
                    status: 'sent_to_ops',
                    userId: params.userId,
                    note: undefined,
                },
            ],
        },
    };
}

export function reservationDataResourcesToDb(
    record: ReservationData,
    action: Action
): CreateReservationResourceInput[] {
    return getAllReservationNotDeletedResources(record.reservation).map((x) =>
        reservationResourceToDb(x, record.reservation.dateTo, record.id, action)
    );
}

export function getAllReservationNotDeletedResources(record: Reservation): EventResource[] {
    let resources = record.resources.filter((x) => isNotDeleted(x));

    if (record.diffCrewForEachEvent ?? false) {
        for (const event of record.events ?? []) {
            if (event.resources) {
                for (const resource of event.resources) {
                    if (!isNotDeleted(resource)) {
                        continue; // skip deleted resources
                    }
                    if (resources.some((x) => x.resource.resourceId === resource.resource.resourceId)) {
                        continue; // skip already added resources
                    }
                    if (resource.resource.type === ResourceType.aircraft) {
                        continue; // skip aircrafts from child events
                    }

                    resources.push(resource);
                }
            }
        }
    }

    return resources;
}

export function reservationResourceToDb(
    { resource }: EventResource,
    reservationDateTo: Date | string,
    reservationId: string,
    action: Action
): CreateReservationResourceInput {
    const dateString =
        reservationDateTo instanceof Date ? reservationDateTo.toISOString() : new Date(reservationDateTo).toISOString();

    return {
        title: resource.title,
        type: resource.type,
        reservationId,
        id: action === Action.create ? uuid.v4() : resource.id,
        contactId: resource.type === ResourceType.aircraft ? undefined : resource.resourceId,
        aircraftId: resource.type === ResourceType.aircraft ? resource.resourceId : undefined,
        externalId: isSome(resource.type) ? undefined : resource.resourceId,
        created: action === Action.create ? new Date().toISOString() : resource.createdAt.toISOString(),
        reservationDateTo: dateString,
    };
}

export function quotationDataResourcesToDb(record: ReservationData, action: Action): CreateQuotationResourceInput[] {
    return record.reservation.resources.map((x) => quotationResourceToDb(x, record.id, action));
}

export function quotationResourceToDb(
    { resource }: EventResource,
    quotationId: string,
    action: Action
): CreateQuotationResourceInput {
    return {
        title: resource.title,
        type: resource.type,
        quotationId,
        id: action === Action.create ? uuid.v4() : resource.id,
        contactId: resource.type === ResourceType.aircraft ? undefined : resource.resourceId,
        aircraftId: resource.type === ResourceType.aircraft ? resource.resourceId : undefined,
        externalId: isSome(resource.type) ? undefined : resource.resourceId,
        created: action === Action.create ? new Date().toISOString() : resource.createdAt.toISOString(),
    };
}

export function createEmptyReservationData(): ReservationData {
    const id = uuid.v4();

    return {
        id,
        createdAt: new Date(),
        deleted: false,
        reservation: {
            id,
            type: EventType.other,
            createdAt: new Date(),
            resources: [],
            dateFrom: new Date(),
            dateTo: new Date(),
            title: '',
            isUnconfirmed: false,
            cancelledLegs: undefined,
        },
        dateFrom: '',
        dateTo: '',
    };
}

export type { EventResource } from '@eflight/shared';

function findFlagOfLevel(
    flags: EventFlags,
    level: EventFlag['level']
): { eventFlag: EventFlag; flagKey: EventFlagKey } | undefined {
    const flagKey = objectKeys(flags).find((flagKey) => flags[flagKey]?.level === level);
    const eventFlag = isSome(flagKey) ? flags[flagKey] : undefined;

    if (isSome(eventFlag) && isSome(flagKey)) {
        return { flagKey, eventFlag };
    }

    return undefined;
}

export function getWorstFlagWithKey(flags?: EventFlags): { eventFlag: EventFlag; flagKey: EventFlagKey } | undefined {
    if (isNone(flags)) {
        return undefined;
    }

    const errorFlag = findFlagOfLevel(flags, 'error');
    const warningFlag = findFlagOfLevel(flags, 'warning');
    const validFlag = findFlagOfLevel(flags, 'valid');

    return errorFlag ?? warningFlag ?? validFlag ?? undefined;
}

export type { EventFlagData } from '@eflight/shared';

export type { EventFlag } from '@eflight/shared';

export type { EventFlags } from '@eflight/shared';

export type EventFlagKey = keyof EventFlags;

export type { DbBaseEvent } from '@eflight/shared';

export type FlightLoading = DbBaseEvent['loading'];

export { EventAttributes } from '@eflight/shared';

export type { FuelPriceRemoteState } from '@eflight/shared';

export type { DbChildEvent } from '@eflight/shared';

export interface DbReservation extends DbBaseEvent {
    events?: DbChildEvent[];
    flightType?: FlightType;
    fuelStart?: number;
    eventsSimple?: Array<{ location: string }>;
    requestedFuel?: number;
    esimatedHours?: number;
    diffCrewForEachEvent?: boolean;
    travel?: TravelData;
}

function dateTimeReviver(key: keyof Confirmation & keyof CreateReservationInput, value: any) {
    if (
        key === 'createdAt' ||
        key === 'dateFrom' ||
        key === 'dateTo' ||
        key === 'time' ||
        key === 'confirmTime' ||
        key === 'timeCreated' ||
        key === 'serviceTime' ||
        key === 'exchDate'
    ) {
        return new Date(value);
    }
    if (key === 'flightOrderSent' || key === 'customerBriefingSent') {
        return {
            ...value,
            date: new Date(value.date),
        };
    }

    return value;
}

export type MaintenanceModel = {
    totalReservationsBefore: number;
    totalMinutesBefore: number;
    totalHoursActual: number;
    totalLandingsActual: number;
    totalCyclesActual: number;
    totalReservationsInPeriod: number;
    totalMinutesInPeriod: number;
    totalLandings: number;
    totalHours: number;
    totalCycles: number;
    maintenanceLandings?: number | null;
    maintenanceHours?: number | null;
    maintenanceCycles?: number | null;
    nextDueMx?: Date | null;
    nextDueMxTolerance?: number | null;
};

export type CalendarFilter = {
    focusResourceId?: string;
    resourceId?: string;
    linesResourceIds?: string[];
    dispatchGroup1ResourceIds?: string[];
    dispatchGroup2ResourceIds?: string[];
};

export type { EmptyLegToRemove } from '@eflight/shared';
